import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useTenantFeatureAccess } from '@/hooks/useTenantFeatureAccess'
import { supabase } from '@/integrations/supabase/client'
import WelcomeCard from '@/components/customer/WelcomeCard'
import CompetitionView from '@/components/customer/CompetitionView'
import { TreeVisualization } from '@/components/customer/TreeVisualization'
import { CityVisualization } from '@/components/customer/CityVisualization'
import { SocialMilestoneNotification } from '@/components/customer/SocialMilestoneNotification'
import { SocialSharingButton } from '@/components/customer/SocialSharingButton'
import VisitStampHistory from '@/components/customer/VisitStampHistory'
import ReferralStampHistory from '@/components/customer/ReferralStampHistory'
import ReferralLinkSelection from '@/components/customer/ReferralLinkSelection'
import LoadingSpinner from '@/components/layout/LoadingSpinner'
import { InstallPrompt } from '@/components/pwa/InstallPrompt'
import { WalletButtons } from '@/components/wallet/WalletButtons'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger, 
  DropdownMenuSeparator 
} from '@/components/ui/dropdown-menu'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useToast } from '@/hooks/use-toast'
import { Trophy, Gift, Star, Check, Crown, ChevronDown, Settings, LogOut, History, TreePine, Users, GraduationCap, Info, Heart } from 'lucide-react'
import RewardDetailModal from '@/components/customer/RewardDetailModal'
import Logo from '@/components/ui/logo'
import Footer from '@/components/layout/Footer'

interface CustomerData {
  id: string
  email: string
  full_name: string
  visit_stamps: number
  referral_stamps: number
  referral_code: string
  total_referrals: number
  total_visits: number
  points: number
  tenant_id: string
}

interface ReferralRewardAssignment {
  id: string
  tenant_id: string
  reward_id: string
  referral_count: number
  is_active: boolean
  reward: {
    id: string
    name: string
    description: string
    reward_type: 'discount' | 'free_item' | 'points' | 'special'
    reward_value: string
    image_url?: string
    admin_approval_required: boolean
  }
}

interface CustomerContribution {
  id: string
  contribution_count: number
  last_contribution_at: string | null
  social_project: {
    id: string
    name: string
    description: string | null
    project_type: 'trees' | 'families' | 'education'
  }
}

interface TenantContributionSummary {
  social_project_id: string
  total_contributions: number
  project_type: string
  project_name: string
}

interface RedemptionRPCResult {
  success: boolean
  error?: string
  redemption_id?: string
}

const getIcon = (rewardType: string) => {
  switch (rewardType) {
    case 'discount': return Star
    case 'free_item': return Gift
    case 'points': return Crown
    case 'special': return Logo
    default: return Logo
  }
}

const getProjectIcon = (type: string) => {
  switch (type) {
    case 'trees': return TreePine;
    case 'families': return Heart;
    case 'education': return GraduationCap;
    default: return Heart;
  }
}

const CustomerDashboard = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { hasVisitRewards, hasReferralRewards, loading: featureLoading } = useTenantFeatureAccess()
  const [customerData, setCustomerData] = useState<CustomerData | null>(null)
  const [referralRewards, setReferralRewards] = useState<ReferralRewardAssignment[]>([])
  const [customerContributions, setCustomerContributions] = useState<CustomerContribution[]>([])
  const [tenantSummaries, setTenantSummaries] = useState<TenantContributionSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedReward, setSelectedReward] = useState<ReferralRewardAssignment | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [customerRedemptions, setCustomerRedemptions] = useState<any[]>([])

  // Helper function to get redemption status
  const getCustomerRedemption = (rewardAssignmentId: string) => {
    return customerRedemptions.find(r => r.reward_assignment_id === rewardAssignmentId)
  }

  const fetchCustomerData = useCallback(async () => {
    if (!user?.id) return

    try {
      console.log('📱 Using secure RPC to get/create customer profile for user:', user.id)
      
      const fullName = `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() || 'Kunde'
      
      // Use the new secure RPC function
      const { data, error } = await supabase.rpc('get_or_create_customer_profile', {
        p_user_id: user.id,
        p_email: user.email || '',
        p_full_name: fullName
      })

      if (error) {
        throw error
      }

      if (data && data.length > 0) {
        const customerData = data[0]
        console.log('✅ Customer data loaded via RPC:', customerData)
        setCustomerData(customerData)
        
        // Fetch referral rewards using RPC
        if (customerData.tenant_id) {
          try {
            const rewardsResult = await supabase.rpc('get_active_referral_rewards', {
              p_tenant_id: customerData.tenant_id
            })

            if (!rewardsResult.error && rewardsResult.data) {
              // Transform the data to match the expected format
              const transformedRewards = rewardsResult.data.map((item: any) => ({
                id: item.id,
                tenant_id: item.tenant_id,
                reward_id: item.reward_id,
                referral_count: item.referral_count,
                is_active: item.is_active,
                reward: item.reward
              }))
              console.log('🎁 Loaded referral rewards via RPC:', transformedRewards)
              setReferralRewards(transformedRewards as ReferralRewardAssignment[])
            }
          } catch (error) {
            console.warn('Could not load referral rewards:', error)
          }

          // Fetch customer redemptions using RPC
          try {
            const redemptionsResult = await supabase.rpc('get_customer_redemptions_secure', {
              p_customer_id: customerData.id
            })

            if (!redemptionsResult.error && redemptionsResult.data) {
              setCustomerRedemptions(redemptionsResult.data)
            }
          } catch (error) {
            console.warn('Could not load customer redemptions:', error)
          }

          // Fetch social contributions using RPC
          try {
            await fetchSocialContributionsRPC(customerData.id, customerData.tenant_id)
          } catch (error) {
            console.warn('Could not load social contributions:', error)
          }
        }
      } else {
        throw new Error('Failed to get or create customer profile')
      }
    } catch (error) {
      console.error('Error fetching customer data:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Kundendaten konnten nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }, [user?.id, user?.email, user?.user_metadata, toast])

  const fetchSocialContributionsRPC = async (customerId: string, tenantId: string) => {
    try {
      const { data: contributions, error: contributionsError } = await supabase.rpc('get_customer_contributions_detailed', {
        p_customer_id: customerId,
        p_tenant_id: tenantId
      })

      if (contributionsError) throw contributionsError

      // Transform the RPC result to match expected format
      const customerContribs = contributions?.map((item: any) => ({
        id: item.contribution_id,
        contribution_count: item.contribution_count,
        last_contribution_at: item.last_contribution_at,
        social_project: {
          id: item.social_project_id,
          name: item.project_name,
          description: null,
          project_type: item.project_type
        }
      })) || []

      const tenantSummaries = contributions?.map((item: any) => ({
        social_project_id: item.social_project_id,
        total_contributions: item.total_contributions,
        project_type: item.project_type,
        project_name: item.project_name
      })) || []

      setCustomerContributions(customerContribs)
      setTenantSummaries(tenantSummaries)
    } catch (error) {
      console.error('Error fetching contributions:', error)
    }
  }

  useEffect(() => {
    fetchCustomerData()
  }, [fetchCustomerData])

  // Set up realtime updates for customer data
  useEffect(() => {
    if (!customerData?.id) return

    console.log('🔄 Setting up realtime updates for customer:', customerData.id)
    
    const channel = supabase
      .channel('customer-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'customers',
          filter: `id=eq.${customerData.id}`
        },
        (payload) => {
          console.log('🔄 Customer data updated in realtime:', payload)
          // Update customer data with new values
          if (payload.new) {
            setCustomerData(prev => prev ? { ...prev, ...payload.new } : null)
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'stamps',
          filter: `customer_id=eq.${customerData.id}`
        },
        (payload) => {
          console.log('🎯 New stamp added in realtime:', payload)
          // Refresh customer data to get updated stamp counts
          fetchCustomerData()
        }
      )
      .subscribe()

    return () => {
      console.log('🔄 Cleaning up realtime subscription')
      supabase.removeChannel(channel)
    }
  }, [customerData?.id, fetchCustomerData])

  const generateReferralCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase()
  }

  const handleRewardClick = (rewardAssignment: ReferralRewardAssignment) => {
    setSelectedReward(rewardAssignment)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedReward(null)
  }

  const handleRewardRedeem = async (rewardAssignment: ReferralRewardAssignment) => {
    if (!customerData) return

    const availableStamps = customerData.referral_stamps
    
    if (availableStamps < rewardAssignment.referral_count) {
      toast({
        variant: "destructive",
        title: "Nicht genügend Empfehlungen",
        description: `Sie benötigen ${rewardAssignment.referral_count} Empfehlungen für diese Belohnung.`,
      })
      return
    }

    try {
      // Use secure RPC function for reward redemption
      const { data, error } = await supabase.rpc('create_customer_reward_redemption_secure', {
        p_customer_id: customerData.id,
        p_reward_assignment_id: rewardAssignment.id
      })

      if (error) {
        console.error('RPC Error:', error)
        throw error
      }

      const result = data as unknown as RedemptionRPCResult
      
      if (!result.success) {
        throw new Error(result.error || 'Unbekannter Fehler bei der Einlösung')
      }

      const statusMessage = rewardAssignment.reward.admin_approval_required 
        ? "Belohnung beantragt! Warten Sie auf die Bestätigung des Administrators."
        : "Belohnung erfolgreich eingelöst!"
      
      toast({
        title: statusMessage,
        description: rewardAssignment.reward.name,
      })

      // Refresh data to update button states  
      await fetchCustomerData()
      closeModal()
    } catch (error: any) {
      console.error('Redemption error:', error)
      let errorMessage = "Die Belohnung konnte nicht eingelöst werden."
      
      if (error.message?.includes('insufficient_referrals')) {
        errorMessage = "Sie haben nicht genügend Empfehlungen für diese Belohnung."
      } else if (error.message?.includes('already_redeemed')) {
        errorMessage = "Diese Belohnung wurde bereits eingelöst."
      } else if (error.message?.includes('not_authorized')) {
        errorMessage = "Sie sind nicht berechtigt, diese Belohnung einzulösen."
      }
      
      toast({
        variant: "destructive",
        title: "Fehler beim Einlösen",
        description: errorMessage,
      })
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  // Calculate referral progress
  const sortedReferralRewards = referralRewards.sort((a, b) => a.referral_count - b.referral_count)
  const currentReferralStamps = customerData?.referral_stamps || 0
  
  const getNextReferralReward = () => {
    return sortedReferralRewards.find(reward => currentReferralStamps < reward.referral_count)
  }
  
  const nextReferralReward = getNextReferralReward()

  const getUserContribution = (projectId: string) => {
    const contribution = customerContributions.find(c => c.social_project.id === projectId)
    return contribution?.contribution_count || 0
  }

  if (loading || featureLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (!customerData) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">Kundenprofil nicht gefunden</h2>
            <p className="text-muted-foreground">
              Es konnte kein Kundenprofil für Ihr Konto gefunden werden.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const firstName = customerData.full_name?.split(' ')[0] || 'Kunde'
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header with Profile and Wallet */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="lg" />
            <h1 className="text-xl font-bold">Herzensempfehlung</h1>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Wallet Button */}
            {(hasVisitRewards || hasReferralRewards) && (
              <WalletButtons passType="visit" className="hidden sm:flex" />
            )}
            
            {/* Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-xs">
                      {firstName.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <div className="flex items-center justify-start gap-2 p-2">
                  <div className="flex flex-col space-y-1 leading-none">
                    <p className="font-medium">{customerData.full_name}</p>
                    <p className="w-[200px] truncate text-sm text-muted-foreground">
                      {customerData.email}
                    </p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/kunde/einstellungen')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Einstellungen
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-7xl space-y-6">
        <InstallPrompt />
        
        {/* 1. Welcome Section */}
        <WelcomeCard 
          customerData={customerData} 
          onViewRewards={hasVisitRewards ? () => navigate('/kunde/praemien') : undefined}
        />

        {/* 2. Referral Rewards Progress Bar */}
        {hasReferralRewards && sortedReferralRewards.length > 0 && (
          <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-background">
            <CardHeader className="pb-3 sm:pb-4">
              <CardTitle className="text-center text-primary mb-2 text-base sm:text-lg md:text-xl">
                Empfehlungs-Prämien Fortschritt
              </CardTitle>
              <CardDescription className="text-center text-xs sm:text-sm">
                Sichere dir zusätzliche Prämien durch erfolgreiche Empfehlungen
              </CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 md:p-6">
              {/* Progress Bar */}
              <div className="relative mb-6 sm:mb-8">
                {/* Background line */}
                <div className="absolute top-4 sm:top-6 left-0 right-0 h-0.5 sm:h-1 bg-muted rounded-full"></div>
                
                {/* Progress line */}
                <div 
                  className="absolute top-4 sm:top-6 left-0 h-0.5 sm:h-1 bg-primary rounded-full transition-all duration-500"
                  style={{
                    width: (() => {
                      // Calculate how many milestones have been reached
                      const completedMilestones = sortedReferralRewards.filter(
                        reward => currentReferralStamps >= reward.referral_count
                      ).length
                      
                      // If no milestones completed, show 0%
                      if (completedMilestones === 0) return '0%'
                      
                      // Calculate the width to the last completed milestone
                      const totalMilestones = sortedReferralRewards.length
                      const progressToLastCompleted = (completedMilestones / totalMilestones) * 100
                      
                      return `${Math.min(progressToLastCompleted, 100)}%`
                    })()
                  }}
                ></div>
                
                {/* Reward milestones */}
                <div className="flex justify-between items-start">
                  {sortedReferralRewards.map((rewardAssignment, index) => {
                    const isReached = currentReferralStamps >= rewardAssignment.referral_count
                    const IconComponent = getIcon(rewardAssignment.reward.reward_type)
                    
                    return (
                      <div key={rewardAssignment.id} className="flex flex-col items-center relative">
                        {/* Milestone circle */}
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full border-2 sm:border-3 flex items-center justify-center mb-2 sm:mb-3 transition-all duration-300 ${
                          isReached 
                            ? 'bg-primary border-primary text-primary-foreground' 
                            : 'bg-background border-muted-foreground/30 text-muted-foreground'
                        }`}>
                          {isReached ? (
                            <Check className="h-3 w-3 sm:h-4 sm:w-4 md:h-6 md:w-6" />
                          ) : (
                            <span className="text-xs sm:text-sm font-bold">{rewardAssignment.referral_count}</span>
                          )}
                        </div>
                        
                        {/* Reward image/icon */}
                        <div 
                          className={`w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-lg border-2 flex items-center justify-center mb-1 sm:mb-2 transition-all duration-300 overflow-hidden cursor-pointer hover:scale-105 ${
                            isReached ? 'border-primary bg-primary/10' : 'border-muted bg-muted/30'
                          }`}
                          onClick={() => handleRewardClick(rewardAssignment)}
                        >
                          {rewardAssignment.reward.image_url ? (
                            <img 
                              src={rewardAssignment.reward.image_url} 
                              alt={rewardAssignment.reward.name}
                              className="w-full h-full object-cover rounded-md"
                              onError={(e) => {
                                console.log('🚫 Image load error:', rewardAssignment.reward.image_url)
                                e.currentTarget.style.display = 'none'
                              }}
                            />
                          ) : (
                            <IconComponent className={`h-4 w-4 sm:h-5 sm:w-5 md:h-8 md:w-8 ${isReached ? 'text-primary' : 'text-muted-foreground'}`} />
                          )}
                        </div>
                        
                        {/* Reward name */}
                        <div className="text-center min-h-[1.5rem] sm:min-h-[2rem] md:min-h-[2.5rem] flex items-center max-w-12 sm:max-w-16 md:max-w-20">
                          <span className={`text-xs sm:text-xs font-medium px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-center ${
                            isReached ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                          }`}>
                            {rewardAssignment.reward.name}
                          </span>
                        </div>
                        
                        {/* Status button */}
                        <div className="mt-1">
                          {getCustomerRedemption(rewardAssignment.id) ? (
                            <button className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-full" disabled>
                              Bereits beantragt
                            </button>
                          ) : isReached ? (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRewardClick(rewardAssignment)
                              }}
                              className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors"
                            >
                              Beantragen
                            </button>
                          ) : (
                            <button className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded-full" disabled>
                              Beantragen
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
              
              {/* Current status */}
              <div className="text-center">
                {nextReferralReward ? (
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Noch <strong className="text-primary">{nextReferralReward.referral_count - currentReferralStamps} Empfehlungen</strong> bis zur nächsten Prämie "{nextReferralReward.reward.name}"
                  </p>
                ) : (
                  <p className="text-xs sm:text-sm text-success font-medium">
                    Herzlichen Glückwunsch! Sie haben alle Empfehlungs-Prämien erreicht!
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 3. Stamp History Dropdown */}
        {(hasVisitRewards || hasReferralRewards) && (
          <Collapsible open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
            <CollapsibleTrigger asChild>
              <Card className="cursor-pointer hover:bg-muted/50 transition-colors">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div className="flex items-center gap-2">
                    <History className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">Stempel-Historie</CardTitle>
                  </div>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isHistoryOpen ? 'rotate-180' : ''}`} />
                </CardHeader>
                <CardDescription className="px-6 pb-6">
                  Ihre erhaltenen Stempel anzeigen
                </CardDescription>
              </Card>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4">
              {hasVisitRewards && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Besuchsstempel</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <VisitStampHistory 
                      customerId={customerData.id} 
                      tenantId={customerData.tenant_id} 
                    />
                  </CardContent>
                </Card>
              )}
              {hasReferralRewards && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Empfehlungsstempel</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ReferralStampHistory 
                      customerId={customerData.id} 
                      tenantId={customerData.tenant_id} 
                    />
                  </CardContent>
                </Card>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* 4. Referral Link Selection */}
        {hasReferralRewards && customerData && (
          <ReferralLinkSelection 
            customerId={customerData.id}
            tenantId={customerData.tenant_id}
            referralCode={customerData.referral_code}
          />
        )}

        {/* 5. Competitions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Wettbewerbe
            </CardTitle>
            <CardDescription>
              Nehmen Sie an spannenden Wettbewerben teil und gewinnen Sie tolle Preise!
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CompetitionView />
          </CardContent>
        </Card>

        {/* 6. Social Engagement */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-red-500" />
              Soziales Engagement
            </CardTitle>
            <CardDescription>
              Durch Ihre Empfehlungen unterstützen Sie automatisch soziale Projekte
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <SocialMilestoneNotification />
            
            {tenantSummaries.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Logo className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="font-semibold mb-2">Keine sozialen Projekte aktiv</h3>
                <p className="text-sm">
                  Derzeit sind keine sozialen Projekte in Ihrem Unternehmen aktiviert.
                </p>
              </div>
            ) : (
              <>
                {/* Info Card */}
                <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-background">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-primary">
                      <Info className="h-5 w-5" />
                      Ihr sozialer Beitrag
                    </CardTitle>
                    <CardDescription>
                      Für jede erfolgreiche Empfehlung leisten Sie automatisch einen Beitrag zu den 
                      aktivierten sozialen Projekten. Ihre goldenen Markierungen zeigen Ihren 
                      persönlichen Beitrag.
                    </CardDescription>
                  </CardHeader>
                </Card>

                {/* Project Visualizations */}
                <div className="grid gap-6 lg:grid-cols-1 xl:grid-cols-2">
                  {tenantSummaries.map((summary) => {
                    const userContribution = getUserContribution(summary.social_project_id)
                    
                    return (
                      <div key={summary.social_project_id}>
                        {summary.project_type === 'trees' && (
                          <TreeVisualization
                            totalTrees={summary.total_contributions}
                            userTrees={userContribution}
                            projectName={summary.project_name}
                          />
                        )}
                        
                        {(summary.project_type === 'families' || summary.project_type === 'family_help') && (
                          <CityVisualization
                            totalHouses={summary.total_contributions}
                            userHouses={userContribution}
                            projectName={summary.project_name}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* Personal Contributions with Sharing */}
                {customerContributions.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-center">Ihr Engagement im Detail</h3>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {customerContributions.map((contribution) => {
                        const Icon = getProjectIcon(contribution.social_project.project_type);
                        return (
                          <Card key={contribution.id} className="text-center">
                            <CardHeader className="pb-3">
                              <div className="flex justify-center mb-2">
                                <Icon className="h-8 w-8 text-primary" />
                              </div>
                              <CardTitle className="text-lg">{contribution.social_project.name}</CardTitle>
                              <CardDescription>
                                {contribution.social_project.description}
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="text-3xl font-bold text-primary mb-2">
                                {contribution.contribution_count}
                              </div>
                              <Badge variant="secondary" className="mb-4">
                                {contribution.social_project.project_type === 'trees' ? 'Bäume' : 
                                 contribution.social_project.project_type === 'families' ? 'Familien unterstützt' :
                                 'Projekte unterstützt'}
                              </Badge>
                              {contribution.last_contribution_at && (
                                <p className="text-xs text-muted-foreground mb-4">
                                  Letzter Beitrag: {new Date(contribution.last_contribution_at).toLocaleDateString('de-DE')}
                                </p>
                              )}
                              <SocialSharingButton 
                                projectName={contribution.social_project.name}
                                contributionCount={contribution.contribution_count}
                                projectType={contribution.social_project.project_type}
                                className="w-full"
                              />
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {customerContributions.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Logo className="h-12 w-12 mx-auto mb-4 text-primary" />
                    <h3 className="font-semibold mb-2">Noch keine Beiträge</h3>
                    <p className="text-sm">
                      Sobald Sie erfolgreich jemanden empfohlen haben, sehen Sie hier Ihren Beitrag 
                      zu den sozialen Projekten.
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Mobile Wallet Button */}
        {(hasVisitRewards || hasReferralRewards) && (
          <div className="sm:hidden">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5" />
                  Digitale Wallet
                </CardTitle>
                <CardDescription>
                  Fügen Sie Ihre Treuekarten zu Apple Wallet oder Google Pay hinzu
                </CardDescription>
              </CardHeader>
              <CardContent>
                <WalletButtons passType="visit" />
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      
      <Footer />
      
      {/* Reward Detail Modal */}
      <RewardDetailModal
        isOpen={isModalOpen}
        onClose={closeModal}
        rewardAssignment={selectedReward}
        canRedeem={selectedReward ? currentReferralStamps >= selectedReward.referral_count : false}
        redemptionStatus={selectedReward ? getCustomerRedemption(selectedReward.id) : null}
        onRedeem={handleRewardRedeem}
        redeeming={false}
        currentStamps={currentReferralStamps}
      />
    </div>
  )
}

export default CustomerDashboard